import { bboxPolygon, booleanIntersects } from '@turf/turf';
import { useEffect, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import type { OrganizationRegion } from '~/common/types';
import rootStore from '~/store';

// Reconstruction Project 2024 - circa Fairfield, CA
const sampleProjectRegion = {
  type: 'MultiPolygon',
  coordinates: [
    [
      [
        [-122.04332047392334, 38.249326429694264],
        [-122.04331535801562, 38.24804279617095],
        [-122.04180786039055, 38.24802437757924],
        [-122.04180424272108, 38.24931800949253],
        [-122.04332047392334, 38.249326429694264],
      ],
    ],
  ],
} as GeoJSON.MultiPolygon;

export const useOutOfRegion = () => {
  const { current: map } = useMap();
  const [isOutOfBounds, setIsOutOfBounds] = useState(false);
  const { currentOrganization } = useSnapshot(rootStore) as typeof rootStore;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    function checkBounds(e: any) {
      const zoom = e.target.getZoom();

      if (zoom < 11) {
        setIsOutOfBounds(false);
        return;
      }

      const bounds = e.target.getBounds();

      if (!bounds) {
        throw new Error('Bounds not found in region');
      }

      const viewportBox = bboxPolygon([
        bounds.getNorthWest().lng,
        bounds.getNorthWest().lat,
        bounds.getSouthEast().lng,
        bounds.getSouthEast().lat,
      ]);

      const inOrganizationBounds = currentOrganization?.regions.some((region: OrganizationRegion) => {
        return booleanIntersects(region.region as GeoJSON.Polygon, viewportBox);
      });

      const inSampleProjectArea = booleanIntersects(sampleProjectRegion, viewportBox);

      const inBounds = inOrganizationBounds || inSampleProjectArea;

      setIsOutOfBounds(!inBounds);
    }
    map?.on('moveend', checkBounds);
    return () => {
      map?.off('moveend', checkBounds);
    };
  }, [currentOrganization]);

  return isOutOfBounds;
};
